/**
 * panelLayout - the floating-panel model, shared by every surface that wants
 * one (research, review, tables).
 *
 * A surface is a full-bleed CANVAS with glass panels docked to its left or
 * right edge, stacked vertically, over the top of it. A panel's width changes
 * nothing but itself; the canvas underneath is constant. That is the whole
 * difference from a tiled layout, where columns push each other around.
 *
 * Everything here is PURE, and generic over the surface's own panel ids: the
 * reducer IS the layout system, so docking, stacking, ordering, front-most and
 * persistence are all testable without a DOM. Each surface supplies a
 * `PanelSurface` - its ids, their size bounds, the default arrangement and its
 * own storage key - and gets the identical behaviour.
 */

export type DockSide = 'left' | 'right';

export interface PanelSlot<Id extends string = string> {
  id: Id;
  dock: DockSide;
  /** Position within the dock, top to bottom. */
  index: number;
  width: number;
  /**
   * This pane's SHARE of its dock column, 0–1.
   *
   * A fraction rather than pixels, because the dock's height is the window's
   * and changes without anyone dragging anything: a stored 400px would mean
   * two-thirds of one viewport and a quarter of another. Fractions survive a
   * resize, a zoom and a different monitor.
   *
   * `undefined` means "no preference" - the column splits evenly. Only the
   * panes on either side of a dragged divider ever get a value, so a column
   * nobody has touched stays even for free.
   */
  height?: number;
  visible: boolean;
}

export interface PanelLayout<Id extends string = string> {
  slots: Record<Id, PanelSlot<Id>>;
  /** Last-touched panel - raised above its siblings. */
  front: Id | null;
}

export interface PanelBounds { min: number; max: number }

/** One surface's configuration. Everything else is generic. */
export interface PanelSurface<Id extends string = string> {
  ids: Id[];
  bounds: Record<Id, PanelBounds>;
  defaults: PanelLayout<Id>;
  /** Versioned: an older model must not be misread as this one. */
  storageKey: string;
}

/** Inset from the viewport edge - what makes a panel read as floating. */
export const DOCK_INSET = 12;

/**
 * The smallest share of a column a pane may be squeezed to.
 *
 * A fraction, for the same reason heights are: 0.12 of any dock leaves the
 * header and a line or two, which is the point below which a pane stops being
 * a pane and becomes a title bar.
 */
export const MIN_PANE_FRACTION = 0.12;

/**
 * More than this in one dock and it scrolls rather than squeezing every
 * panel into an unreadable sliver.
 */
export const COMFORTABLE_STACK = 2;

/* ------------------------------------------------------------------ */
/*  Operations                                                         */
/* ------------------------------------------------------------------ */

const clamp = (value: number, { min, max }: PanelBounds): number =>
  Math.min(max, Math.max(min, value));

/** Resize one floating panel. Nothing else moves - that is the point. */
export function resizePanel<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  id: Id,
  width: number
): PanelLayout<Id> {
  const slot = layout.slots[id];
  if (!slot) return layout;
  const next = clamp(Math.round(width), surface.bounds[id]);
  if (next === slot.width) return layout;
  return { ...layout, slots: { ...layout.slots, [id]: { ...slot, width: next } } };
}

export function setPanelVisible<Id extends string>(
  layout: PanelLayout<Id>,
  id: Id,
  visible: boolean
): PanelLayout<Id> {
  const slot = layout.slots[id];
  if (!slot || slot.visible === visible) return layout;
  const next: PanelLayout<Id> = {
    ...layout,
    slots: { ...layout.slots, [id]: { ...slot, visible } },
  };
  // Opening a panel brings it forward; closing the front one releases it.
  if (visible) return { ...next, front: id };
  return { ...next, front: layout.front === id ? null : layout.front };
}

export function togglePanel<Id extends string>(
  layout: PanelLayout<Id>,
  id: Id
): PanelLayout<Id> {
  return setPanelVisible(layout, id, !layout.slots[id]?.visible);
}

/** Raise a panel above its dock siblings. */
export function bringToFront<Id extends string>(
  layout: PanelLayout<Id>,
  id: Id
): PanelLayout<Id> {
  if (!layout.slots[id] || layout.front === id) return layout;
  return { ...layout, front: id };
}

/**
 * Move a panel to a dock, at a position in that dock's stack.
 *
 * Indices are renumbered densely afterwards so a dock never develops gaps or
 * ties - two panels claiming index 0 would render in arbitrary order.
 */
export function movePanel<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  id: Id,
  dock: DockSide,
  index: number
): PanelLayout<Id> {
  const slot = layout.slots[id];
  if (!slot) return layout;

  const destination = surface.ids
    .filter((other) => other !== id && layout.slots[other].dock === dock)
    .sort((a, b) => layout.slots[a].index - layout.slots[b].index);

  const at = clamp(index, { min: 0, max: destination.length });
  destination.splice(at, 0, id);

  const slots = { ...layout.slots };
  destination.forEach((panelId, position) => {
    slots[panelId] = { ...slots[panelId], dock, index: position };
  });

  // The dock it left is renumbered too, for the same reason.
  surface.ids
    .filter((other) => other !== id && slots[other].dock === slot.dock && slot.dock !== dock)
    .sort((a, b) => slots[a].index - slots[b].index)
    .forEach((panelId, position) => {
      slots[panelId] = { ...slots[panelId], index: position };
    });

  /*
    Both columns' stored splits described a different set of panes a moment
    ago, so they no longer mean anything. Clearing them returns each to an
    even split rather than stretching an old ratio over a new column.
  */
  const moved: PanelLayout<Id> = { ...layout, slots, front: id };
  return clearDockHeights(surface, clearDockHeights(surface, moved, dock), slot.dock);
}

/**
 * The resolved height fractions for a dock's visible panes, top to bottom.
 *
 * Always sums to 1 and always has one entry per visible pane, whatever the
 * stored layout says. Panes with no stored height share whatever the ones
 * with a height leave over - so a column where one divider has been dragged
 * behaves, and a column nobody has touched is even.
 */
export function dockHeights<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  dock: DockSide
): number[] {
  const panes = panelsInDock(surface, layout, dock);
  if (panes.length === 0) return [];
  if (panes.length === 1) return [1];

  const stored = panes.map((p) => (typeof p.height === 'number' && p.height > 0 ? p.height : null));
  const known = stored.filter((h): h is number => h !== null);

  // Nobody has dragged anything here: even split.
  if (known.length === 0) return panes.map(() => 1 / panes.length);

  const claimed = known.reduce((sum, h) => sum + h, 0);
  const unset = stored.length - known.length;

  // Every pane has a stored height - normalise in case they drifted.
  if (unset === 0) {
    const total = claimed || 1;
    return stored.map((h) => (h as number) / total);
  }

  // Some do, some do not: the rest divide the remainder, never below the floor.
  const remainder = Math.max(unset * MIN_PANE_FRACTION, 1 - claimed);
  const each = remainder / unset;
  const raw = stored.map((h) => (h === null ? each : h));
  const total = raw.reduce((sum, h) => sum + h, 0) || 1;
  return raw.map((h) => h / total);
}

/**
 * Drag the divider BELOW the pane at `boundaryIndex`.
 *
 * Only the two panes either side move; everything above and below keeps its
 * share, which is what makes a multi-pane column feel like separate dividers
 * rather than one elastic stack. `delta` is a fraction of the column - the
 * caller converts pixels using the dock's measured height.
 *
 * Both panes are clamped to `MIN_PANE_FRACTION`, so a divider stops rather
 * than collapsing its neighbour to nothing.
 */
export function resizeDockSplit<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  dock: DockSide,
  boundaryIndex: number,
  delta: number
): PanelLayout<Id> {
  const panes = panelsInDock(surface, layout, dock);
  if (boundaryIndex < 0 || boundaryIndex >= panes.length - 1) return layout;

  const heights = dockHeights(surface, layout, dock);
  const above = heights[boundaryIndex];
  const below = heights[boundaryIndex + 1];
  const pair = above + below;

  // The pair's total is fixed: what one gains the other loses.
  const nextAbove = Math.min(
    pair - MIN_PANE_FRACTION,
    Math.max(MIN_PANE_FRACTION, above + delta)
  );
  if (Math.abs(nextAbove - above) < 0.0005) return layout;
  const nextBelow = pair - nextAbove;

  /*
    Write back EVERY pane's resolved height, not just the two that moved.
    Otherwise the untouched ones stay `undefined` and `dockHeights` would
    re-divide the remainder among them on the next read, quietly undoing part
    of the drag.
  */
  const slots = { ...layout.slots };
  panes.forEach((pane, i) => {
    const height = i === boundaryIndex ? nextAbove : i === boundaryIndex + 1 ? nextBelow : heights[i];
    slots[pane.id] = { ...slots[pane.id], height };
  });
  return { ...layout, slots };
}

/**
 * Forget a dock's stored heights, returning it to an even split.
 *
 * Used when a pane joins or leaves a column: the remembered split described a
 * different set of panes, and stretching it over a new one gives arbitrary
 * results.
 */
export function clearDockHeights<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  dock: DockSide
): PanelLayout<Id> {
  const slots = { ...layout.slots };
  let touched = false;
  surface.ids.forEach((id) => {
    if (slots[id].dock === dock && slots[id].height !== undefined) {
      slots[id] = { ...slots[id], height: undefined };
      touched = true;
    }
  });
  return touched ? { ...layout, slots } : layout;
}

/** Visible panels in a dock, top to bottom. */
export function panelsInDock<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  dock: DockSide
): PanelSlot<Id>[] {
  return surface.ids
    .map((id) => layout.slots[id])
    .filter((slot) => slot.visible && slot.dock === dock)
    .sort((a, b) => a.index - b.index);
}

/** True when this dock holds more panels than fit calmly and should scroll. */
export function dockScrolls<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  dock: DockSide
): boolean {
  return panelsInDock(surface, layout, dock).length > COMFORTABLE_STACK;
}

/**
 * The ONE width every panel on a side renders at.
 *
 * Panels each remember a width of their own - that is what a panel is worth
 * when it sits alone, or after it moves to an empty side. But a DOCK is a
 * column, and a column of three panels at three different widths reads as
 * broken, so the side renders at a single shared width.
 *
 * The widest wants to win (a document panel dragged next to settings should
 * not shrink to settings' width), but only as far as every panel in the
 * column can actually go: the shared width is clamped to the tightest bounds
 * in the dock - the largest minimum and the smallest maximum - so no panel is
 * rendered outside its own limits.
 */
export function dockWidth<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  dock: DockSide
): number {
  const panels = panelsInDock(surface, layout, dock);
  if (panels.length === 0) return 0;
  if (panels.length === 1) return panels[0].width;

  const min = Math.max(...panels.map((p) => surface.bounds[p.id].min));
  const max = Math.min(...panels.map((p) => surface.bounds[p.id].max));
  const widest = Math.max(...panels.map((p) => p.width));

  // A dock whose bounds do not overlap (min > max) cannot satisfy everyone;
  // the floor wins, so the narrowest panel is still usable.
  if (min > max) return min;
  return Math.min(Math.max(widest, min), max);
}

/**
 * How far the transcript's reading column must be inset on one side.
 *
 * The canvas itself stays full-bleed - chat runs edge to edge under the glass
 * - but the column of text is kept clear of whatever is docked over it, so
 * nothing readable is ever hidden behind a panel.
 */
export function readingInset<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  dock: DockSide
): number {
  const width = dockWidth(surface, layout, dock);
  // The inset follows the width actually rendered, or the reading column
  // would clear a width no panel is drawn at.
  return width === 0 ? 0 : width + DOCK_INSET * 2;
}

/** Stacking order for a panel: the front-most one wins. */
export function panelZIndex<Id extends string>(
  layout: PanelLayout<Id>,
  id: Id
): number {
  return layout.front === id ? 40 : 30;
}

/* ------------------------------------------------------------------ */
/*  Persistence                                                        */
/* ------------------------------------------------------------------ */

/**
 * Repair anything a stored layout got wrong.
 *
 * Storage is untrusted input: it may predate a panel, name one that no longer
 * exists (the v1 model had a `transcript` panel), or carry a width from a
 * previous set of bounds. Everything degrades to a usable arrangement rather
 * than rendering a broken surface.
 */
export function validateLayout<Id extends string>(
  surface: PanelSurface<Id>,
  raw: unknown
): PanelLayout<Id> {
  if (!raw || typeof raw !== 'object') return surface.defaults;
  const candidate = raw as Partial<PanelLayout<Id>>;

  const slots = {} as Record<Id, PanelSlot<Id>>;
  surface.ids.forEach((id) => {
    const fallback = surface.defaults.slots[id];
    const stored = (candidate.slots as any)?.[id];
    slots[id] = {
      id,
      dock: stored?.dock === 'left' || stored?.dock === 'right' ? stored.dock : fallback.dock,
      index:
        typeof stored?.index === 'number' && Number.isFinite(stored.index) && stored.index >= 0
          ? Math.floor(stored.index)
          : fallback.index,
      width:
        typeof stored?.width === 'number' && Number.isFinite(stored.width)
          ? clamp(Math.round(stored.width), surface.bounds[id])
          : fallback.width,
      /*
        A share of the column, so anything outside (0, 1] is not a height —
        it is corruption, an older model, or a pixel value from a build that
        stored pixels. Dropping it falls back to the even split, which is
        always safe.
      */
      height:
        typeof stored?.height === 'number'
        && Number.isFinite(stored.height)
        && stored.height > 0
        && stored.height <= 1
          ? stored.height
          : undefined,
      visible: typeof stored?.visible === 'boolean' ? stored.visible : fallback.visible,
    };
  });

  // Renumber each dock densely, so restored indices can never tie.
  (['left', 'right'] as DockSide[]).forEach((dock) => {
    surface.ids
      .filter((id) => slots[id].dock === dock)
      .sort((a, b) => slots[a].index - slots[b].index)
      .forEach((id, position) => {
        slots[id] = { ...slots[id], index: position };
      });
  });

  const front =
    candidate.front && surface.ids.includes(candidate.front as Id)
      ? (candidate.front as Id)
      : null;

  return { slots, front };
}

export function loadLayout<Id extends string>(
  surface: PanelSurface<Id>,
  storage?: Pick<Storage, 'getItem'>
): PanelLayout<Id> {
  try {
    const store = storage ?? window.localStorage;
    const raw = store.getItem(surface.storageKey);
    if (!raw) return surface.defaults;
    return validateLayout(surface, JSON.parse(raw));
  } catch {
    return surface.defaults;
  }
}

export function saveLayout<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  storage?: Pick<Storage, 'setItem'>
): void {
  try {
    const store = storage ?? window.localStorage;
    store.setItem(surface.storageKey, JSON.stringify(layout));
  } catch {
    // Layout is a convenience; failing to persist it is not worth an error.
  }
}

/* ------------------------------------------------------------------ */
/*  Narrow viewports                                                   */
/* ------------------------------------------------------------------ */

/** Tailwind's `lg`. Below this a panel is a sheet, not a float. */
export const SINGLE_COLUMN_BELOW = 1024;

export function isSingleColumn(viewportWidth: number): boolean {
  return viewportWidth < SINGLE_COLUMN_BELOW;
}

/**
 * The one panel a narrow viewport shows.
 *
 * Two sheets stacked on a phone is nonsense, so the front-most visible panel
 * wins and the rest stay closed until it is dismissed. Stored widths and dock
 * sides are ignored here - and NOT written back, so widening the window
 * restores the desktop arrangement exactly as it was.
 */
export function activeSheet<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>
): Id | null {
  const visible = surface.ids.filter((id) => layout.slots[id].visible);
  if (visible.length === 0) return null;
  if (layout.front && visible.includes(layout.front)) return layout.front;
  return visible[0];
}
